import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { Utilities } from 'library/ts/utilities/Utilities';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { ITagValidationResult } from './SmartTagOperationsTypes';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Shared tag validation utilities for SmartTag operations
 *
 * Provides reusable validation patterns to reduce code duplication across
 * operations classes while maintaining consistent validation logic.
 */
export class TagValidationUtils {
	/**
	 * @description Validates if a tag exists and is not undefined
	 */
	static isTagDefined( tag: ITag, utilities?: Utilities ): boolean {
		return !utilities?.Type?.isUndefined?.( tag );
	}

	/**
	 * @description Validates tag structure with defensive programming against potential runtime errors
	 * Uses comprehensive null checking and exception handling to prevent runtime failures
	 * when tag API structure is malformed or partially initialized.
	 */
	static isValidTagStructure( tag: ITag, utilities?: Utilities ): boolean {
		try {
			return !utilities?.Type?.isUndefined?.( tag?.API ) &&
				!utilities?.Type?.isUndefined?.( tag?.API?.TAG ) &&
				utilities?.Type?.isFunction?.( tag?.API?.TAG?.reset );
		} catch ( error ) {
			return false;
		}
	}

	/**
	 * @description Comprehensive tag validation for removal operations
	 * Combines existence check and structure validation with clear error messaging
	 */
	static validateTagForRemoval( tag: ITag, utilities?: Utilities ): ITagValidationResult {
		if ( !TagValidationUtils.isTagDefined( tag, utilities ) ) {
			return {
				isValid: false,
				error: 'Failure: Tag instance is undefined. Request to remove rejected.'
			};
		}

		if ( !TagValidationUtils.isValidTagStructure( tag, utilities ) ) {
			return {
				isValid: false,
				error: 'Failure: Tag API structure is invalid. Cannot reset tag.'
			};
		}

		return { isValid: true };
	}

	/**
	 * @description Validates tag structure for reset operations
	 * Similar to removal validation but with reset-specific error messaging
	 */
	static validateTagForReset( tag: ITag, utilities?: Utilities ): boolean {
		return TagValidationUtils.isTagDefined( tag, utilities ) &&
			TagValidationUtils.isValidTagStructure( tag, utilities );
	}

	/**
	 * @description Validates whether a resolved AssetEmbedType is safe to use for playback.
	 *
	 * This is the single source of truth for the "CID is not playable" rule.
	 * It is intentionally kept as a pure static method with no side-effects so it can be
	 * called from any layer of the stack (TagModel, AviaFactory, etc.) without coupling
	 * those layers to each other.
	 *
	 * Rules enforced:
	 *  - `undefined` → more than one asset identifier was set simultaneously, or none at all.
	 *  - `AssetEmbedType.CID` → contentId-based playback is not supported; only PID and assetURL are valid.
	 *
	 * @param {AssetEmbedType | undefined} embedType - Result of TagContentUtilities.assetEmbedType()
	 * @returns {ITagValidationResult}
	 */
	static isValidEmbedType( embedType: AssetEmbedType | undefined ): ITagValidationResult {
		if ( embedType === undefined ) {
			return {
				isValid: false,
				error: 'ERROR: multiple assets (pid, assetURL, contentId) are set in params or not set at all. Make sure only one is set while others are undefined! Stop execution!'
			};
		}

		if ( embedType === AssetEmbedType.CID ) {
			return {
				isValid: false,
				error: 'ERROR: contentId is not a supported playback identifier in Smart Tag. Use pid or assetURL instead. Stop execution!'
			};
		}

		return { isValid: true };
	}
}